import React, { useState, useEffect } from "react";
import Subtasks from "./Subtasks";
import { normalizeText } from "../utils/textUtils";
import { updateTask } from "../api/povinnostiApi";
import { normalizeCompany } from "../utils/companyUtils";

// Odklad o mesiace (1/2/3) je zo zákona možný iba pre daňové priznania
const isDanovePriznanieTask = (task) => normalizeText(task?.text || '').includes('DANOVE PRIZNAN');

export default function TasksTable({
  tasks,
  setTasks,
  loading,
  error,
  userPin,
  saveTasks,
  editId,
  setEditId,
  editValues,
  setEditValues,
  saveEdit,
  cancelEdit,
  formatDateDMY,
  companyList = [],
  addToCompanyList,
  taskList = [],
  addToTaskList,
}) {
  const [newSubtaskText, setNewSubtaskText] = useState({});
  const [filters, setFilters] = useState({ company: "", text: "", deadline: "" });
  const [sort, setSort] = useState({ field: 'deadline', direction: "asc" });
  const [showCompleted, setShowCompleted] = useState(false);
  const [expandedSubtasks, setExpandedSubtasks] = useState(new Set());
  const [autoExpandedId, setAutoExpandedId] = useState(null);
  const [selectedTaskIds, setSelectedTaskIds] = useState([]);
  const [bulkDate, setBulkDate] = useState("");
  const [bulkSubtaskText, setBulkSubtaskText] = useState("");
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [noteInput, setNoteInput] = useState("");
  const [editingDeadlineId, setEditingDeadlineId] = useState(null);
  const [odkladOpenId, setOdkladOpenId] = useState(null);
  const [dismissedOdkladIds, setDismissedOdkladIds] = useState([]);

  // Pomocná funkcia na kontrolu stavu (vytiahnutá hore, aby bola dostupná všade)
  const isTaskDone = (completed) => completed === true || completed === 1 || String(completed) === "true";

  const toggleSubtaskPanel = (taskId) => {
    setExpandedSubtasks(prev => {
      const next = new Set(prev);
      if (next.has(taskId)) {
        next.delete(taskId);
      } else {
        next.add(taskId);
      }
      return next;
    });
  };

  // --- 1. POMOCNÉ FUNKCIE ---
  const getNextDate = (current, interval) => {
    if (!current || !interval) return current;
    const d = new Date(current);
    if (interval === 'monthly') d.setMonth(d.getMonth() + 1);
    else if (interval === 'weekly') d.setDate(d.getDate() + 7);
    else if (interval === 'yearly' || interval === 'annually') d.setFullYear(d.getFullYear() + 1);
    else if (!isNaN(Number(interval))) d.setDate(d.getDate() + Number(interval));
    return d.toISOString().slice(0, 10);
  };

  const formatIntervalSK = (interval) => {
    if (!interval) return '';
    const mapping = { monthly: 'mesačne', weekly: 'týždenne', yearly: 'ročne', annually: 'ročne', none: 'žiadny' };
    return mapping[interval] || (!isNaN(Number(interval)) ? `každých ${interval} dní` : interval);
  };

  // --- 2. HANDLERY ---
  const handleDeleteTask = (taskId) => {
    if (!window.confirm('Naozaj chcete vymazať túto úlohu?')) return;
    const updated = tasks.filter(t => t.id !== taskId);
    setTasks(updated);
    saveTasks(userPin, updated);
  };

  const handleCopyTask = (task) => {
    const newTask = { ...task, id: Date.now(), completed: false, subtasks: (task.subtasks || []).map(s => ({ ...s, id: Date.now() + Math.random() })) };
    const updated = [...tasks, newTask];
    setTasks(updated);
    saveTasks(userPin, updated);
  };

  const handleToggleCompleted = (task) => {
    if (isTaskDone(task.completed)) {
      const updatedTasks = tasks.map(t =>
        t.id === task.id ? { ...t, completed: false } : t
      );
      setTasks(updatedTasks);
      saveTasks(userPin, updatedTasks);
    } else {
      completeTask(task, tasks);
    }
  };

  const validateAndSaveEdit = (id) => {
    if (!editValues.text?.trim()) {
      alert("Názov úlohy nemôže byť prázdny.");
      return;
    }
    const normalizedCompany = normalizeCompany(editValues.company);
    if (normalizedCompany && !companyList.includes(normalizedCompany)) {
      const ok = window.confirm(`Firma "${normalizedCompany}" nie je v zozname. Pridať ju?`);
      if (!ok) return;
      if (addToCompanyList) addToCompanyList(normalizedCompany);
    }
    if (normalizedCompany) setEditValues(prev => ({ ...prev, company: normalizedCompany }));
    const taskName = editValues.text?.trim();
    if (taskName && !taskList.includes(taskName)) {
      const ok = window.confirm(`Povinnosť "${taskName}" nie je v zozname. Pridať ju?`);
      if (!ok) return;
      if (addToTaskList) addToTaskList(taskName);
    }
    // Use setTimeout to let state update before saving
    setTimeout(() => saveEdit(id), 0);
  };

  const toggleSelectTask = (id) => {
    setSelectedTaskIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const handleBulkEditDate = () => {
    if (!bulkDate || selectedTaskIds.length === 0) return;
    const updated = tasks.map(t => selectedTaskIds.includes(t.id) ? { ...t, deadline: bulkDate } : t);
    setTasks(updated);
    saveTasks(userPin, updated);
    setBulkDate("");
    setSelectedTaskIds([]);
  };

  const handleBulkAddSubtaskFn = () => {
    if (!bulkSubtaskText.trim() || selectedTaskIds.length === 0) return;
    const lines = bulkSubtaskText.split('\n').map(s => s.trim()).filter(Boolean);
    const updated = tasks.map(t => {
      if (!selectedTaskIds.includes(t.id)) return t;
      const newST = lines.map(text => ({
        id: `st-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        text,
        completed: false,
        trackedTime: 0
      }));
      return { ...t, subtasks: [...(t.subtasks || []), ...newST] };
    });
    setTasks(updated);
    saveTasks(userPin, updated);
    setBulkSubtaskText("");
    setSelectedTaskIds([]);
  };

  const handleBulkDelete = () => {
    if (!window.confirm(`Naozaj vymazať ${selectedTaskIds.length} úloh?`)) return;
    const updated = tasks.filter(t => !selectedTaskIds.includes(t.id));
    setTasks(updated);
    saveTasks(userPin, updated);
    setSelectedTaskIds([]);
  };

  const saveNote = (taskId) => {
    const updated = tasks.map(t => t.id === taskId ? { ...t, note: noteInput } : t);
    setTasks(updated);
    saveTasks(userPin, updated);
    setEditingNoteId(null);
    setNoteInput("");
  };

  const handleInlineDeadlineChange = (taskId, newDate) => {
    if (!newDate) return;
    const updated = tasks.map(t => t.id === taskId ? { ...t, deadline: newDate } : t);
    setTasks(updated);
    saveTasks(userPin, updated);
  };

  // --- 3. PODÚLOHY ---
  const handleAddSubtask = (taskId) => {
    const text = (newSubtaskText[taskId] || '').trim();
    if (!text) return;
    const updatedTasks = tasks.map(task => {
      if (task.id !== taskId) return task;
      return {
        ...task,
        subtasks: [...(task.subtasks || []), {
          id: `st-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`,
          text, completed: false, trackedTime: 0
        }]
      };
    });
    setTasks(updatedTasks);
    saveTasks(userPin, updatedTasks);
    setNewSubtaskText(prev => ({ ...prev, [taskId]: '' }));
  };

  const handleToggleSubtask = (taskId, subtaskId) => {
    const updatedTasks = tasks.map(task => {
      if (task.id !== taskId) return task;
      const newSubtasks = (task.subtasks || []).map(st =>
        st.id === subtaskId ? { ...st, completed: !st.completed } : st
      );
      return { ...task, subtasks: newSubtasks };
    });
    setTasks(updatedTasks);

    const changedTask = updatedTasks.find(t => t.id === taskId);
    if (changedTask?.subtasks?.length > 0) {
      const allDone = changedTask.subtasks.every(st => isTaskDone(st.completed));
      if (allDone && !isTaskDone(changedTask.completed)) {
        completeTask(changedTask, updatedTasks);
        return;
      }
    }
    saveTasks(userPin, updatedTasks);
  };

  const handleDeleteSubtask = (taskId, subtaskId) => {
    const updatedTasks = tasks.map(task => {
      if (task.id !== taskId) return task;
      return { ...task, subtasks: (task.subtasks || []).filter(st => st.id !== subtaskId) };
    });
    setTasks(updatedTasks);
    saveTasks(userPin, updatedTasks);
  };

  // --- 4. EXPORT A AUTOMATIZÁCIA ---
  const handleSubtaskExport = async (taskId, subtask) => {
    const parentTask = tasks.find(t => t.id === taskId);
    if (!parentTask) return;
    const now = new Date();
    const totalMinutes = subtask.trackedTime || 0;
    const hours = totalMinutes / 60;
    const entry = {
      id: String(Date.now()) + Math.random().toString(36).slice(2),
      companyName: (parentTask.company || 'NEZNÁMA').toUpperCase(),
      date: now.toISOString().slice(0, 10),
      description: subtask.text,
      service: 'Práca',
      quantity: hours > 0 ? Number(hours.toFixed(2)) : 1,
      unitPrice: 50,
      total: Math.round(hours * 50 * 100) / 100,
      note: subtask.note || '',
      trackedTime: totalMinutes,
      createdAt: now.toISOString(),
    };
    try {
      const r = await fetch(`/php/work_tracking_api.php?action=add_entry&pin=${encodeURIComponent(userPin)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ entry }),
      });
      const data = await r.json();
      if (data.success) {
        const newTask = {
          ...parentTask,
          subtasks: (parentTask.subtasks || []).map(st =>
            st.id === subtask.id ? { ...st, completed: true } : st
          )
        };
        await updateTask(userPin, newTask);
        setTasks(prev => prev.map(t => t.id === taskId ? newTask : t));
        alert('Podúloha exportovaná do evidencie práce.');
      } else {
        alert('Chyba pri exporte: ' + (data.message || 'Neznáma chyba'));
      }
    } catch (e) {
      alert('Chyba siete pri exporte podúlohy.');
    }
  };

  const exportToWorkLog = async (task) => {
    const now = new Date();
    const totalMinutes = task.subtasks?.length > 0
      ? task.subtasks.reduce((sum, st) => sum + (Number(st.trackedTime) || 0), 0)
      : (Number(task.estimatedTime) || 0);

    const hours = totalMinutes / 60;
    const entry = {
      id: String(Date.now()),
      companyName: (task.company || 'NEZNÁMA').toUpperCase(),
      date: now.toISOString().slice(0, 10),
      description: task.text,
      service: 'Práca',
      quantity: hours > 0 ? Number(hours.toFixed(2)) : 1,
      unitPrice: 50,
      total: Math.round(hours * 50 * 100) / 100,
      note: task.note || '',
      trackedTime: totalMinutes,
      createdAt: now.toISOString(),
    };

    try {
      const r = await fetch(`/php/work_tracking_api.php?action=add_entry&pin=${encodeURIComponent(userPin)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ entry }),
      });
      return await r.json();
    } catch (e) {
      console.error("Chyba exportu:", e);
      return { success: false };
    }
  };

  const completeTask = async (task, currentTasks) => {
    if (!window.confirm("Úloha je hotová. Exportovať do evidencie a uzavrieť/posunúť?")) return;

    const interval = task.interval || task.repeatInterval;
    let newTasksList;

    if (interval && interval !== 'none') {
      const updatedTask = {
        ...task,
        completed: false,
        deadline: getNextDate(task.deadline, interval),
        interval: interval,
        subtasks: (task.subtasks || []).map(st => ({ ...st, completed: false, trackedTime: 0 }))
      };
      const historyEntry = {
        ...task,
        id: `${task.id}_hist_${Date.now()}`,
        completed: true,
        trackedTime: 0,
      };
      newTasksList = [...currentTasks.map(t => t.id === task.id ? updatedTask : t), historyEntry];
    } else {
      const updatedTask = { ...task, completed: true };
      newTasksList = currentTasks.map(t => t.id === task.id ? updatedTask : t);
    }

    if (!task.noExport) await exportToWorkLog(task);
    await saveTasks(userPin, newTasksList);
    setTasks(newTasksList);
  };

  // --- 5. FILTRE A SORTING ---
  const uniqueCompanies = Array.from(new Set(
    tasks
      .filter(t => !isTaskDone(t.completed) && !String(t.id).includes("_hist_"))
      .map(t => (t.company || "").trim())
      .filter(Boolean)
  )).sort((a, b) => a.localeCompare(b, 'sk'));
  const uniqueYears = Array.from(new Set(tasks.map(t => (t.deadline || "").slice(0, 4)).filter(y => /^\d{4}$/.test(y)))).sort();

  const filteredTasks = tasks.filter(task => {
    if (isTaskDone(task.completed)) return false;
    const matchesCompany = !filters.company || normalizeText(task.company || "").includes(normalizeText(filters.company));
    const matchesText = !filters.text || normalizeText(task.text || "").includes(normalizeText(filters.text));
    const matchesDeadline = !filters.deadline || (task.deadline || "").includes(filters.deadline);
    return matchesCompany && matchesText && matchesDeadline;
  });

  const completedTasks = showCompleted ? tasks.filter(task => {
    if (!isTaskDone(task.completed)) return false;
    const matchesCompany = !filters.company || normalizeText(task.company || "").includes(normalizeText(filters.company));
    const matchesText = !filters.text || normalizeText(task.text || "").includes(normalizeText(filters.text));
    return matchesCompany && matchesText;
  }) : [];

  const sortedTasks = [...filteredTasks].sort((a, b) => {
    const getVal = (obj, field) => {
      if (field === 'deadline') return Date.parse(obj[field] || '') || 0;
      return (obj[field] || '').toString().toLowerCase();
    };
    const aVal = getVal(a, sort.field);
    const bVal = getVal(b, sort.field);
    if (aVal !== bVal) {
      if (sort.direction === "asc") return aVal < bVal ? -1 : 1;
      else return aVal > bVal ? -1 : 1;
    }
    // sekundárne radenie: deadline asc, potom firma asc
    const aDate = Date.parse(a.deadline || '') || 0;
    const bDate = Date.parse(b.deadline || '') || 0;
    if (aDate !== bDate) return aDate - bDate;
    return (a.company || '').toLowerCase().localeCompare((b.company || '').toLowerCase());
  });

  // Automaticky rozbaľ prvú úlohu v zozname
  useEffect(() => {
    const firstId = sortedTasks[0]?.id;
    if (!firstId || firstId === autoExpandedId) return;
    setAutoExpandedId(firstId);
    setExpandedSubtasks(prev => new Set([...prev, firstId]));
  }, [sortedTasks[0]?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // --- 6. ŠTÝLY ---
  const thS = (extra) => ({ padding: '10px 12px', textAlign: 'left', background: '#f1f5f9', color: '#475569', fontWeight: 600, fontSize: 13, position: 'sticky', top: 0, zIndex: 2, borderBottom: '2px solid #e2e8f0', ...extra });
  const thS2 = (extra) => ({ padding: '6px 8px', background: '#f8fafc', position: 'sticky', top: 43, zIndex: 2, borderBottom: '1px solid #e2e8f0', ...extra });
  
  const handleOdklad = (taskId, months) => {
    const original = tasks.find(t => t.id === taskId);
    if (!original) return;
    const originalDeadline = original.odkladOriginalDeadline || original.deadline;
    const base = new Date(originalDeadline);
    base.setMonth(base.getMonth() + months);
    const newDeadline = base.toISOString().slice(0, 10);

    // 1. Pôvodná úloha sa premenuje na "Podanie odkladu ..."
    // 2. Nová úloha = pôvodná povinnosť s novým termínom
    const updated = tasks.map(t => {
      if (t.id !== taskId) return t;
      return {
        ...t,
        text: t.odkladOriginalText || `Podanie odkladu: ${t.text}`,
        odkladOriginalText: t.odkladOriginalText || t.text,
        odkladOriginalDeadline: originalDeadline,
        odkladMonths: months,
      };
    });

    // Nová úloha pre samotnú povinnosť po odklade
    const newTask = {
      ...original,
      id: Date.now().toString() + Math.random().toString(36).slice(2),
      text: original.odkladOriginalText || original.text,
      deadline: newDeadline,
      completed: false,
      odkladOriginalDeadline: undefined,
      odkladOriginalText: undefined,
      odkladMonths: undefined,
      odkladPodany: undefined,
      odkladSourceId: taskId,
    };

    const final = [...updated, newTask];
    setTasks(final);
    saveTasks(userPin, final);
    setOdkladOpenId(null);
  };

  const handleCancelOdklad = (taskId) => {
    const original = tasks.find(t => t.id === taskId);
    // Odstrán aj vygenerovanú úlohu po odklade
    const updated = tasks
      .filter(t => t.odkladSourceId !== taskId)
      .map(t => {
        if (t.id !== taskId) return t;
        const { odkladOriginalDeadline, odkladOriginalText, odkladMonths, odkladPodany, ...rest } = t;
        return { ...rest, text: odkladOriginalText || t.text, deadline: odkladOriginalDeadline || t.deadline };
      });
    setTasks(updated);
    saveTasks(userPin, updated);
  };

  const deadlineBadge = (deadline) => {
    if (!deadline) return null;
    const d = new Date(deadline);
    const today = new Date();
    const dDate = new Date(d.getFullYear(), d.getMonth(), d.getDate());
    const tDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const diffDays = (dDate - tDate) / 86400000;
    let color, bg;
    if (diffDays < 0)       { color = '#dc2626'; bg = '#fee2e2'; }
    else if (diffDays === 0) { color = '#d97706'; bg = '#fef3c7'; }
    else                     { color = '#16a34a'; bg = '#dcfce7'; }
    return <span style={{ background: bg, color, borderRadius: 6, padding: '2px 8px', fontWeight: 600, fontSize: 12, whiteSpace: 'nowrap' }}>{formatDateDMY(deadline)}</span>;
  };
  
  const intervalBadge = (interval) => {
    if (!interval || interval === 'none') return <span style={{ color: '#94a3b8', fontSize: 12 }}>—</span>;
    const colors = { weekly: '#7c3aed', monthly: '#0369a1', annually: '#b45309', quarterly: '#be185d' };
    const c = colors[interval] || '#475569';
    return <span style={{ background: c + '18', color: c, borderRadius: 6, padding: '2px 8px', fontSize: 12, fontWeight: 600 }}>{formatIntervalSK(interval)}</span>;
  };

  return (
    <div style={{ background: 'white', borderRadius: 16, margin: '1.5rem auto', maxWidth: '100%', padding: '20px', boxShadow: '0 2px 16px rgba(0,0,0,0.09)' }}>

      {/* Proaktívny banner: odklad pre oneskorené nesplnené úlohy */}
      {(() => {
        const today = new Date();
        const tDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());
        const candidates = (tasks || []).filter(t =>
          !isTaskDone(t.completed) &&
          !String(t.id).includes('_hist_') &&
          !t.odkladOriginalDeadline &&
          !dismissedOdkladIds.includes(t.id) &&
          t.deadline &&
          new Date(new Date(t.deadline).getFullYear(), new Date(t.deadline).getMonth(), new Date(t.deadline).getDate()) <= tDate &&
          isDanovePriznanieTask(t)
        );
        if (candidates.length === 0) return null;
        return (
          <div style={{ background: '#fffbeb', border: '1px solid #fbbf24', borderRadius: 10, padding: '12px 16px', marginBottom: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <span style={{ fontWeight: 700, fontSize: 13, color: '#b45309' }}>📅 Chcete podať odklad pre tieto úlohy?</span>
              <button
                onClick={() => setDismissedOdkladIds(prev => [...prev, ...candidates.map(t => t.id)])}
                style={{ fontSize: 11, padding: '2px 8px', background: 'transparent', border: 'none', cursor: 'pointer', color: '#94a3b8' }}
                title="Zatvoriť upozornenie"
              >✕ Zatvoriť</button>
            </div>
            {candidates.map(t => (
              <div key={t.id} style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', padding: '5px 0', borderTop: '1px solid #fef3c7' }}>
                <span style={{ fontSize: 12, color: '#1e293b', fontWeight: 600, minWidth: 160 }}>
                  {t.company && <span style={{ background: '#e0f2fe', color: '#0369a1', borderRadius: 4, padding: '1px 6px', fontSize: 11, marginRight: 5 }}>{t.company}</span>}
                  {t.text}
                </span>
                <span style={{ fontSize: 11, color: '#94a3b8' }}>({formatDateDMY(t.deadline)})</span>
                {[1, 2, 3].map(m => (
                  <button
                    key={m}
                    onClick={() => { handleOdklad(t.id, m); setDismissedOdkladIds(prev => [...prev, t.id]); }}
                    style={{ fontSize: 11, padding: '2px 10px', background: '#fef3c7', color: '#b45309', border: '1px solid #fbbf24', borderRadius: 5, cursor: 'pointer', fontWeight: 700 }}
                  >+{m}M</button>
                ))}
                <button
                  onClick={() => setDismissedOdkladIds(prev => [...prev, t.id])}
                  style={{ fontSize: 11, padding: '2px 7px', background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 5, cursor: 'pointer', color: '#64748b' }}
                  title="Odklad nepotrebujem"
                >Nie</button>
              </div>
            ))}
          </div>
        );
      })()}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: '#1e293b' }}>📋 Zoznam úloh</h3>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => setShowCompleted(s => !s)} style={{ fontSize: 13, background: showCompleted ? '#16a34a' : '#64748b', color: 'white', border: 'none', borderRadius: 8, padding: '5px 12px', cursor: 'pointer' }}>
            {showCompleted ? '✅ Splnené: ON' : '☑ Splnené: OFF'}
          </button>
          <button onClick={() => setFilters({ company: "", text: "", deadline: "" })} style={{ fontSize: 13, background: '#f1f5f9', border: '1px solid #cbd5e1', borderRadius: 8, padding: '5px 12px', cursor: 'pointer', color: '#475569' }}>
            ✕ Vymazať filtre
          </button>
        </div>
      </div>

      {loading && <div style={{ color: '#64748b', padding: 20, textAlign: 'center' }}>Načítavam dáta...</div>}
      {error && <div style={{ color: 'red', padding: 8 }}>Chyba: {error}</div>}

      {selectedTaskIds.length > 0 && (
        <div style={{ background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 10, padding: '12px 16px', marginBottom: 12, display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
          <span style={{ fontWeight: 700, color: '#0369a1', fontSize: 14 }}>✓ {selectedTaskIds.length} vybraných</span>
          <button onClick={() => setSelectedTaskIds([])} style={{ fontSize: 12, background: '#e0f2fe', color: '#0369a1', border: 'none', borderRadius: 6, padding: '4px 10px', cursor: 'pointer' }}>Zrušiť výber</button>
          <span style={{ color: '#94a3b8', fontSize: 12, margin: '0 4px' }}>|</span>
          <input type="date" value={bulkDate} onChange={e => setBulkDate(e.target.value)} style={{ fontSize: 12, border: '1px solid #bae6fd', borderRadius: 6, padding: '4px 8px' }} />
          <button onClick={handleBulkEditDate} disabled={!bulkDate} style={{ fontSize: 12, background: '#0ea5e9', color: 'white', border: 'none', borderRadius: 6, padding: '4px 10px', cursor: bulkDate ? 'pointer' : 'not-allowed', opacity: bulkDate ? 1 : 0.5 }}>Zmeniť termín</button>
          <span style={{ color: '#94a3b8', fontSize: 12, margin: '0 4px' }}>|</span>
          <textarea placeholder="Podúlohy (každá na riadok)..." value={bulkSubtaskText} onChange={e => setBulkSubtaskText(e.target.value)} rows={3} style={{ fontSize: 12, border: '1px solid #bae6fd', borderRadius: 6, padding: '4px 8px', minWidth: 220, resize: 'vertical', verticalAlign: 'top' }} />
          <button onClick={handleBulkAddSubtaskFn} disabled={!bulkSubtaskText.trim()} style={{ fontSize: 12, background: '#0ea5e9', color: 'white', border: 'none', borderRadius: 6, padding: '4px 10px', cursor: bulkSubtaskText.trim() ? 'pointer' : 'not-allowed', opacity: bulkSubtaskText.trim() ? 1 : 0.5 }}>+ Podúlohy</button>
          <button onClick={handleBulkDelete} style={{ fontSize: 12, background: '#fee2e2', color: '#dc2626', border: 'none', borderRadius: 6, padding: '4px 10px', cursor: 'pointer', marginLeft: 'auto' }}>🗑 Vymazať vybrané</button>
        </div>
      )}

      {(!tasks || tasks.length === 0) ? (
        <div style={{ color: '#94a3b8', padding: 32, textAlign: 'center' }}>Žiadne úlohy.</div>
      ) : (
        <div style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: '72vh' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr>
                <th style={thS({ width: 36, textAlign: 'center', color: '#16a34a', fontSize: 16 })} title="Hotovo">✓</th>
                <th style={thS({ width: 36, textAlign: 'center', cursor: 'default' })}>
                  <input type="checkbox" checked={sortedTasks.length > 0 && sortedTasks.every(t => selectedTaskIds.includes(t.id))} onChange={() => { const allSel = sortedTasks.every(t => selectedTaskIds.includes(t.id)); setSelectedTaskIds(allSel ? [] : sortedTasks.map(t => t.id)); }} style={{ width: 15, height: 15, cursor: 'pointer', accentColor: '#0ea5e9' }} title="Vybrať všetky" />
                </th>
                <th style={thS({ cursor: 'pointer', minWidth: 110 })} onClick={() => setSort(s => ({ field: 'company', direction: s.field === 'company' && s.direction === 'asc' ? 'desc' : 'asc' }))}>Firma {sort.field === 'company' ? (sort.direction === 'asc' ? '▲' : '▼') : '↕'}</th>
                <th style={thS({ cursor: 'pointer', minWidth: 160 })} onClick={() => setSort(s => ({ field: 'text', direction: s.field === 'text' && s.direction === 'asc' ? 'desc' : 'asc' }))}>Názov {sort.field === 'text' ? (sort.direction === 'asc' ? '▲' : '▼') : '↕'}</th>
                <th style={thS({ cursor: 'pointer', minWidth: 75, width: 75 })} onClick={() => setSort(s => ({ field: 'deadline', direction: s.field === 'deadline' && s.direction === 'asc' ? 'desc' : 'asc' }))}>Termín {sort.field === 'deadline' ? (sort.direction === 'asc' ? '▲' : '▼') : '↕'}</th>
                <th style={thS({ minWidth: 100 })}>Interval</th>
                <th style={thS({ minWidth: 60 })}>Čas</th>
                <th style={thS({ minWidth: 90 })}>Podúlohy</th>
                <th style={thS({ minWidth: 90 })}>Akcie</th>
              </tr>
              <tr>
                <th style={thS2({ width: 36 })}></th>
                <th style={thS2({ width: 36 })}></th>
                <th style={thS2()}>
                  <select value={filters.company} onChange={e => setFilters({...filters, company: e.target.value})} style={{ width: '100%', fontSize: 12, border: '1px solid #e2e8f0', borderRadius: 6, padding: '3px 6px' }}>
                    <option value="">Všetky</option>
                    {uniqueCompanies.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </th>
                <th style={thS2()}><input placeholder="Hľadať..." value={filters.text} onChange={e => setFilters({...filters, text: e.target.value})} style={{ width: '94%', fontSize: 12, border: '1px solid #e2e8f0', borderRadius: 6, padding: '3px 6px' }} /></th>
                <th style={thS2()}>
                  <select value={filters.deadline} onChange={e => setFilters({...filters, deadline: e.target.value})} style={{ width: '100%', fontSize: 12, border: '1px solid #e2e8f0', borderRadius: 6, padding: '3px 6px' }}>
                    <option value="">Všetky roky</option>
                    {uniqueYears.map(y => <option key={y} value={y}>{y}</option>)}
                  </select>
                </th>
                <th style={thS2()} colSpan={4}></th>
              </tr>
            </thead>
            <tbody>
              {sortedTasks.map((task, taskIdx) => {
                const isExpanded = expandedSubtasks.has(task.id);
                const stList = task.subtasks || [];
                const doneCount = stList.filter(st => isTaskDone(st.completed)).length;
                const isOverdue = task.deadline && new Date(task.deadline) < new Date();
                const done = isTaskDone(task.completed);
                const isFirst = taskIdx === 0;

                return (
                  <React.Fragment key={task.id}>
                    <tr style={{
                      background: isFirst ? '#eff6ff' : (isOverdue ? '#fff8f8' : 'white'),
                      borderBottom: isFirst ? '2px solid #93c5fd' : '1px solid #f1f5f9',
                      fontWeight: isFirst ? 700 : 'normal',
                      boxShadow: isFirst ? 'inset 3px 0 0 #3b82f6' : 'none',
                      transition: 'background 0.15s',
                    }}>
                      {editId === task.id ? (
                        <>
                          <td style={{ padding: '6px 8px', textAlign: 'center' }}></td>
                          <td style={{ padding: '6px 8px', textAlign: 'center' }}>
                            <input type="checkbox" checked={selectedTaskIds.includes(task.id)} onChange={() => toggleSelectTask(task.id)} style={{ width: 15, height: 15, cursor: 'pointer', accentColor: '#0ea5e9' }} />
                          </td>
                          <td style={{ padding: '6px 8px' }}>
                            <input
                              list="company-list-table"
                              value={editValues.company || ""}
                              onChange={e => setEditValues({...editValues, company: e.target.value.toUpperCase()})}
                              onBlur={e => setEditValues(prev => ({...prev, company: normalizeCompany(e.target.value)}))}
                              onKeyDown={e => { if (e.key === 'Enter') validateAndSaveEdit(task.id); if (e.key === 'Escape') cancelEdit(); }}
                              style={{ width: '100%', border: '1px solid #cbd5e1', borderRadius: 6, padding: '4px 8px' }}
                            />
                            <datalist id="company-list-table">
                              {companyList.map(c => <option key={c} value={c} />)}
                            </datalist>
                          </td>
                          <td style={{ padding: '6px 8px' }}>
                            <input
                              list="task-list-table"
                              value={editValues.text || ""}
                              onChange={e => setEditValues({...editValues, text: e.target.value})}
                              onKeyDown={e => { if (e.key === 'Enter') validateAndSaveEdit(task.id); if (e.key === 'Escape') cancelEdit(); }}
                              style={{ width: '100%', border: '1px solid #cbd5e1', borderRadius: 6, padding: '4px 8px' }}
                            />
                            <datalist id="task-list-table">
                              {taskList.map(t => <option key={t} value={t} />)}
                            </datalist>
                          </td>
                          <td style={{ padding: '6px 8px' }}><input type="date" value={editValues.deadline || ""} onChange={e => setEditValues({...editValues, deadline: e.target.value})} onKeyDown={e => { if (e.key === 'Enter') validateAndSaveEdit(task.id); if (e.key === 'Escape') cancelEdit(); }} style={{ width: '100%', border: '1px solid #cbd5e1', borderRadius: 6, padding: '4px 8px' }} /></td>
                          <td style={{ padding: '6px 8px' }}><select value={editValues.interval || "none"} onChange={e => setEditValues({...editValues, interval: e.target.value})} onKeyDown={e => { if (e.key === 'Enter') validateAndSaveEdit(task.id); if (e.key === 'Escape') cancelEdit(); }} style={{ border: '1px solid #cbd5e1', borderRadius: 6, padding: '4px 6px', fontSize: 13 }}><option value="none">Žiadny</option><option value="weekly">Týždenne</option><option value="monthly">Mesačne</option><option value="annually">Ročne</option></select></td>
                          <td style={{ padding: '6px 8px' }}><input type="number" value={editValues.estimatedTime || ""} onChange={e => setEditValues({...editValues, estimatedTime: e.target.value})} onKeyDown={e => { if (e.key === 'Enter') validateAndSaveEdit(task.id); if (e.key === 'Escape') cancelEdit(); }} style={{ width: 60, border: '1px solid #cbd5e1', borderRadius: 6, padding: '4px 8px' }} placeholder="min" /></td>
                          <td style={{ padding: '6px 8px' }}><input value={editValues.note || ""} onChange={e => setEditValues({...editValues, note: e.target.value})} onKeyDown={e => { if (e.key === 'Enter') validateAndSaveEdit(task.id); if (e.key === 'Escape') cancelEdit(); }} style={{ width: '100%', border: '1px solid #cbd5e1', borderRadius: 6, padding: '4px 8px', fontSize: 12 }} placeholder="Poznámka..." /></td>
                          <td style={{ padding: '6px 8px' }}>
                            <button onClick={() => validateAndSaveEdit(task.id)} style={{ background: '#0ea5e9', color: 'white', border: 'none', borderRadius: 6, padding: '4px 12px', cursor: 'pointer', fontSize: 13 }}>Uložiť</button>
                            <button onClick={cancelEdit} style={{ background: '#f1f5f9', border: 'none', borderRadius: 6, padding: '4px 10px', cursor: 'pointer', fontSize: 13, marginLeft: 4, color: '#64748b' }}>✕</button>
                          </td>
                        </>
                      ) : (
                        <>
                          <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                            <input type="checkbox" checked={done} onChange={() => handleToggleCompleted(task)} style={{ width: 17, height: 17, cursor: 'pointer', accentColor: '#16a34a' }} />
                          </td>
                          <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                            <input type="checkbox" checked={selectedTaskIds.includes(task.id)} onChange={() => toggleSelectTask(task.id)} style={{ width: 15, height: 15, cursor: 'pointer', accentColor: '#0ea5e9' }} />
                          </td>
                          <td style={{ padding: '10px 12px' }}>
                            <span style={{ background: '#e0f2fe', color: '#0369a1', borderRadius: 6, padding: '2px 8px', fontSize: 12, fontWeight: 600 }}>{task.company}</span>
                          </td>
                          <td style={{ padding: '10px 12px', fontWeight: 600, color: '#1e293b' }}>
                            {task.text}
                            {editingNoteId === task.id ? (
                              <div style={{ display: 'flex', gap: 4, marginTop: 4 }}>
                                <input
                                  autoFocus
                                  value={noteInput}
                                  onChange={e => setNoteInput(e.target.value)}
                                  onKeyDown={e => { if (e.key === 'Enter') saveNote(task.id); if (e.key === 'Escape') { setEditingNoteId(null); setNoteInput(''); } }}
                                  style={{ flex: 1, fontSize: 12, border: '1px solid #cbd5e1', borderRadius: 6, padding: '3px 7px' }}
                                  placeholder="Poznámka..."
                                />
                                <button onClick={() => saveNote(task.id)} style={{ background: '#0ea5e9', color: 'white', border: 'none', borderRadius: 5, padding: '2px 8px', cursor: 'pointer', fontSize: 12 }}>OK</button>
                                <button onClick={() => { setEditingNoteId(null); setNoteInput(''); }} style={{ background: '#f1f5f9', border: 'none', borderRadius: 5, padding: '2px 6px', cursor: 'pointer', fontSize: 12 }}>✕</button>
                              </div>
                            ) : (
                              task.note && <div style={{ fontWeight: 400, fontSize: 12, color: '#64748b', marginTop: 2 }}>📝 {task.note}</div>
                            )}
                          </td>
                          <td style={{ padding: '10px 12px' }}>
                            {editingDeadlineId === task.id ? (
                              <input
                                type="date"
                                autoFocus
                                defaultValue={task.deadline || ""}
                                onBlur={e => { handleInlineDeadlineChange(task.id, e.target.value); setEditingDeadlineId(null); }}
                                onKeyDown={e => {
                                  if (e.key === 'Enter') { handleInlineDeadlineChange(task.id, e.target.value); setEditingDeadlineId(null); }
                                  if (e.key === 'Escape') setEditingDeadlineId(null);
                                }}
                                style={{ border: '1px solid #0ea5e9', borderRadius: 6, padding: '3px 7px', fontSize: 13, outline: 'none' }}
                              />
                            ) : (
                              <span onClick={() => setEditingDeadlineId(task.id)} style={{ cursor: 'pointer' }} title="Kliknúť pre editáciu termínu">
                                {deadlineBadge(task.deadline)}
                              </span>
                            )}
                            {task.odkladOriginalDeadline && (
                              <div style={{ fontSize: 11, color: '#b45309', marginTop: 3 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
                                  <span>📅 odklad +{task.odkladMonths}m</span>
                                  <span style={{ color: '#94a3b8' }}>(pôv. termín: {formatDateDMY(task.odkladOriginalDeadline)})</span>
                                  <button
                                    onClick={() => handleCancelOdklad(task.id)}
                                    title="Zrušiť odklad (vymaže aj vytvorenú úlohu)"
                                    style={{ fontSize: 10, padding: '0 4px', border: '1px solid #e2e8f0', borderRadius: 4, cursor: 'pointer', background: '#fef3c7', color: '#b45309', lineHeight: '15px' }}
                                  >✕ zrušiť</button>
                                </div>
                                {task.odkladOriginalText && (
                                  <div style={{ color: '#64748b', marginTop: 2 }}>
                                    ↳ vytvorená úloha: <strong>{task.odkladOriginalText}</strong>
                                  </div>
                                )}
                                <label style={{ display: 'flex', alignItems: 'center', gap: 5, marginTop: 3, cursor: 'pointer',
                                  color: task.odkladPodany ? '#16a34a' : '#b45309', fontWeight: task.odkladPodany ? 700 : 400 }}>
                                  <input
                                    type="checkbox"
                                    checked={!!task.odkladPodany}
                                    onChange={() => {
                                      const updated = tasks.map(t =>
                                        t.id === task.id ? { ...t, odkladPodany: !t.odkladPodany } : t
                                      );
                                      setTasks(updated);
                                      saveTasks(userPin, updated);
                                    }}
                                    style={{ accentColor: '#16a34a', cursor: 'pointer' }}
                                  />
                                  {task.odkladPodany ? '✅ Odklad podaný' : 'Odklad podaný?'}
                                </label>
                              </div>
                            )}
                          </td>
                          <td style={{ padding: '10px 12px' }}>{intervalBadge(task.interval || task.repeatInterval)}</td>
                          <td style={{ padding: '10px 12px', color: '#64748b', fontSize: 13 }}>{task.estimatedTime ? `${task.estimatedTime}m` : '—'}</td>
                          <td style={{ padding: '10px 12px' }}>
                            <button onClick={() => toggleSubtaskPanel(task.id)} style={{ background: isExpanded ? '#0ea5e9' : '#f1f5f9', color: isExpanded ? 'white' : '#475569', border: 'none', borderRadius: 8, padding: '3px 10px', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
                              {stList.length > 0 ? `${doneCount}/${stList.length}` : '+'} {isExpanded ? '▲' : '▼'}
                            </button>
                          </td>
                          <td style={{ padding: '10px 12px' }}>
                            <div style={{ display: 'flex', gap: 4 }}>
                              <button title="Upraviť" onClick={() => { setEditId(task.id); setEditValues({...task}); }} style={{ background: '#f1f5f9', border: 'none', borderRadius: 6, padding: '4px 8px', cursor: 'pointer', fontSize: 14 }}>✏️</button>
                              <button title="Kopírovať" onClick={() => handleCopyTask(task)} style={{ background: '#f1f5f9', border: 'none', borderRadius: 6, padding: '4px 8px', cursor: 'pointer', fontSize: 14 }}>📋</button>
                              <button title="Poznámka" onClick={() => { setEditingNoteId(task.id); setNoteInput(task.note || ''); }} style={{ background: task.note ? '#fef9c3' : '#f1f5f9', border: 'none', borderRadius: 6, padding: '4px 8px', cursor: 'pointer', fontSize: 14 }}>📝</button>
                              <button title={task.noExport ? 'Export vypnutý' : 'Exportovať pri dokončení'} onClick={() => { const updated = tasks.map(t => t.id === task.id ? { ...t, noExport: !t.noExport } : t); setTasks(updated); saveTasks(userPin, updated); }} style={{ background: task.noExport ? '#fef3c7' : '#f1f5f9', border: task.noExport ? '1px solid #fbbf24' : 'none', borderRadius: 6, padding: '4px 8px', cursor: 'pointer', fontSize: 14 }} title={task.noExport ? 'Export vypnutý – kliknúť pre zapnutie' : 'Export zapnutý – kliknúť pre vypnutie'}>🚫</button>
              <button title="Vymazať" onClick={() => handleDeleteTask(task.id)} style={{ background: '#fee2e2', border: 'none', borderRadius: 6, padding: '4px 8px', cursor: 'pointer', fontSize: 14 }}>🗑</button>
                              {isDanovePriznanieTask(task) && (
                                <button
                                  title="Odklad termínu (len daňové priznanIa)"
                                  onClick={() => setOdkladOpenId(odkladOpenId === task.id ? null : task.id)}
                                  style={{ background: task.odkladOriginalDeadline ? '#fef3c7' : '#f1f5f9', border: task.odkladOriginalDeadline ? '1px solid #fbbf24' : 'none', borderRadius: 6, padding: '4px 8px', cursor: 'pointer', fontSize: 14 }}
                                >📅</button>
                              )}
                            </div>
                            {isDanovePriznanieTask(task) && odkladOpenId === task.id && (
                              <div style={{ display: 'flex', gap: 4, marginTop: 5, flexWrap: 'wrap' }}>
                                <span style={{ fontSize: 11, color: '#64748b', alignSelf: 'center', marginRight: 2 }}>odklad:</span>
                                {[1, 2, 3].map(m => (
                                  <button
                                    key={m}
                                    onClick={() => handleOdklad(task.id, m)}
                                    style={{
                                      fontSize: 11, padding: '2px 8px',
                                      background: task.odkladMonths === m ? '#b45309' : '#fef3c7',
                                      color: task.odkladMonths === m ? '#fff' : '#b45309',
                                      border: '1px solid #fbbf24', borderRadius: 5, cursor: 'pointer', fontWeight: 600
                                    }}
                                  >+{m}M</button>
                                ))}
                                <button
                                  onClick={() => setOdkladOpenId(null)}
                                  style={{ fontSize: 11, padding: '2px 6px', background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 5, cursor: 'pointer', color: '#64748b' }}
                                >✕</button>
                              </div>
                            )}
                          </td>
                        </>
                      )}
                    </tr>
                    {isExpanded && (
                      <tr>
                        <td colSpan={9} style={{ padding: '8px 20px 16px', background: isFirst ? '#dbeafe' : '#f8fafc', borderBottom: isFirst ? '2px solid #93c5fd' : '2px solid #e2e8f0', boxShadow: isFirst ? 'inset 3px 0 0 #3b82f6' : 'none' }}>
                          <Subtasks
                            subtasks={stList}
                            taskId={task.id}
                            onToggle={subtaskId => handleToggleSubtask(task.id, subtaskId)}
                            onDelete={subtaskId => handleDeleteSubtask(task.id, subtaskId)}
                            onExport={(subtask) => handleSubtaskExport(task.id, subtask)}
                            onReorder={(updatedST) => {
                              const newTasks = tasks.map(t => t.id === task.id ? { ...t, subtasks: updatedST } : t);
                              setTasks(newTasks);
                              saveTasks(userPin, newTasks);
                            }}

                          />
                          <div style={{ display: 'flex', marginTop: 10, gap: 6 }}>
                            <input
                              placeholder="Nová podúloha..."
                              value={newSubtaskText[task.id] || ''}
                              onChange={e => setNewSubtaskText({ ...newSubtaskText, [task.id]: e.target.value })}
                              onKeyDown={e => e.key === 'Enter' && handleAddSubtask(task.id)}
                              style={{ fontSize: 13, flex: 1, border: '1px solid #cbd5e1', borderRadius: 8, padding: '5px 10px' }}
                            />
                            <button onClick={() => handleAddSubtask(task.id)} style={{ background: '#0ea5e9', color: 'white', border: 'none', borderRadius: 8, padding: '5px 14px', cursor: 'pointer', fontWeight: 700, fontSize: 15 }}>+</button>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
              {completedTasks.length > 0 && (
                <tr><td colSpan={9} style={{ padding: '10px 12px 4px', color: '#94a3b8', fontSize: 12, fontWeight: 600, borderTop: '2px dashed #e2e8f0' }}>SPLNENÉ</td></tr>
              )}
              {completedTasks.map(task => (
                <tr key={task.id} style={{ background: '#f8fafc', opacity: 0.6 }}>
                  <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                    <input type="checkbox" checked={true} onChange={() => handleToggleCompleted(task)} style={{ width: 16, height: 16, cursor: 'pointer', accentColor: '#16a34a' }} />
                  </td>
                  <td style={{ padding: '8px 12px' }}></td>
                  <td style={{ padding: '8px 12px' }}><span style={{ background: '#f1f5f9', color: '#94a3b8', borderRadius: 6, padding: '2px 8px', fontSize: 12 }}>{task.company}</span></td>
                  <td style={{ padding: '8px 12px', textDecoration: 'line-through', color: '#94a3b8' }}>{task.text}</td>
                  <td style={{ padding: '8px 12px', color: '#94a3b8', fontSize: 12 }}>{formatDateDMY(task.deadline)}</td>
                  <td style={{ padding: '8px 12px' }}>{intervalBadge(task.interval || task.repeatInterval)}</td>
                  <td></td>
                  <td></td>
                  <td style={{ padding: '8px 12px' }}>
                    <button onClick={() => handleDeleteTask(task.id)} style={{ background: '#fee2e2', border: 'none', borderRadius: 6, padding: '4px 8px', cursor: 'pointer', fontSize: 14 }}>🗑</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}